import React, { useEffect, useCallback, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { POPULAR } from "../data/motorcycles";
import { formatIDR } from "../lib/whatsapp";

export default function BestsellerCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    duration: 24,
  });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => {
      if (!document.hidden) emblaApi.scrollNext();
    }, 3800);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section id="terpopuler" className="relative pt-4 pb-8 md:pt-6 md:pb-12 bg-gradient-to-b from-slate-50 to-white" data-testid="section-popular">
      <div className="y-container">
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#e60012] text-white">
              <Flame className="w-4 h-4" />
            </span>
            <h2 className="font-display font-extrabold text-lg sm:text-2xl text-slate-900">
              Motor Terpopuler
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            <button
              onClick={prev}
              data-testid="popular-prev"
              aria-label="Sebelumnya"
              className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#002d62] hover:bg-[#002d62] hover:text-white text-slate-600 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              data-testid="popular-next"
              aria-label="Selanjutnya"
              className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#002d62] hover:bg-[#002d62] hover:text-white text-slate-600 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef} data-testid="popular-carousel">
          <div className="flex gap-3 md:gap-4">
            {POPULAR.map((m, i) => (
              <div
                key={m.slug}
                className="shrink-0 basis-[70%] xs:basis-[55%] sm:basis-[38%] md:basis-[28%] lg:basis-[22%]"
              >
                <PopularCard motor={m} active={i === selected} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-1.5 mt-4" data-testid="popular-dots">
          {snaps.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Ke slide ${i + 1}`}
              className={`transition-all rounded-full ${
                i === selected ? "w-6 h-1.5 bg-[#e60012]" : "w-1.5 h-1.5 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PopularCard({ motor, active }) {
  return (
    <motion.div
      animate={{ scale: active ? 1 : 0.97, opacity: active ? 1 : 0.85 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl bg-white border border-slate-200 overflow-hidden h-full"
    >
      <Link to={`/motor/${motor.slug}`} className="block">
        <div className="aspect-[4/3] bg-white relative overflow-hidden">
          <img
            src={motor.image}
            alt={motor.name}
            loading="lazy"
            className="w-full h-full object-contain p-3"
          />
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#e60012] text-white text-[10px] font-bold">
            <Flame className="w-3 h-3" /> POPULER
          </span>
        </div>
        <div className="px-3 pb-3">
          <p className="font-display font-bold text-sm text-slate-900 line-clamp-1">{motor.name}</p>
          <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{motor.priceLabel}</p>
          <p className="font-display font-extrabold text-sm text-[#002d62] mt-0.5">
            {formatIDR(motor.price_idr)}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}
